import { DEFAULT_TARGET, isIdfTarget, type IdfTargets } from "idfweb-build";
import { targetNameOfChip } from "idfweb-chip";

/**
 * The chip target selection and how a detected board changes it.
 *
 * The device pane's dropdown holds a `Selection`: `AUTO` or a target name.
 * The targets are the bundled ESP-IDF's, known once it is unpacked. The
 * effect of a detected chip on the selection is computed here as a value, so
 * the pane only carries it out and the rule is testable without a serial port.
 */
export type Selection = string;

export const AUTO = "auto";

export { DEFAULT_TARGET, type IdfTargets };

let auto = DEFAULT_TARGET;

/** The target `AUTO` stands for: ESP-IDF's default unless the deployment sets another. */
export function autoTarget(): string {
    return auto;
}

export function setAutoTarget(target: string): void {
    auto = target;
}

/** The target a build uses for this selection. */
export function effectiveTarget(selection: Selection): string {
    return selection === AUTO ? auto : selection;
}

export type BoardDecision =
    /** ESP-IDF has no such target; the selection is unchanged. */
    | { kind: "unknown" }
    /** The board matches the selected target. */
    | { kind: "keep"; target: string }
    /** The selection was auto; set it to the board's target. */
    | { kind: "adopt"; target: string }
    /** The selected target differs from the board's; ask before changing it. */
    | { kind: "confirm"; from: string; to: string };

/** The effect on the selection of a board that esptool-js identified as `chipName`. */
export function decideOnBoard(selection: Selection, chipName: string, targets: IdfTargets): BoardDecision {
    const target = targetNameOfChip(chipName);
    if (!isIdfTarget(targets, target)) return { kind: "unknown" };
    if (selection === AUTO) return { kind: "adopt", target };
    if (selection === target) return { kind: "keep", target };
    return { kind: "confirm", from: selection, to: target };
}
